<!-- #################################################################################### -->
<!-- ###### HERINCO                                                                ###### -->
<!-- ###### @author: John David Vásquez Serna                                      ###### -->
<!-- ###### @date: Junio 2025                                                      ###### -->
<!-- #################################################################################### -->

<!-- #################################################################################### -->
<!-- ###### Sección de HTML                                                        ###### -->
<!-- #################################################################################### -->
<template>
  <v-main class="contenido d-flex align-center">

    <div class="d-flex justify-center align-center">
      <v-alert outlined type="info" color="primary" dense hide-details>
        La contraseña debe estar compuesta por 6 números.
      </v-alert>
    </div>

    <v-card class="elevation-0 d-flex justify-center">
      <v-card-text style="width: 17rem;">
        <validation-observer ref="observerContrasena" v-slot="{ invalid }">
          <v-form class="mt-3" lazy-validation>
            <div>
                <validation-provider v-slot="{ errors }" :rules="{ required: true, min: 6 }" >
                  <v-text-field v-model="contrasena" label="Nueva contraseña" maxlength="6" dense outlined @paste.prevent :counter="6"
                    :type="show2 ? 'text' : 'password'" :append-icon="show2 ? 'visibility' : 'visibility_off'"
                    @click:append="show2 = !show2" required :error-messages="errors" @keydown.space.prevent @keypress="validarLetra">
                  </v-text-field>
                </validation-provider>
            </div>                         
            <div>
              <validation-provider v-slot="{ errors }" :rules="{ required: true, contrasenaValida: true }" ref="confirmarContrasena">
                <v-text-field v-model="confirmarContrasena" label="Confirmar contraseña"
                  :type="show3 ? 'text' : 'password'" :append-icon="show3 ? 'visibility' : 'visibility_off'" maxlength="6" @keypress="validarLetra" :counter="6"
                  @click:append="show3 = !show3" required dense outlined :error-messages="errors" @keydown.space.prevent :disabled="contrasena === '' || contrasena === null">
                </v-text-field>
              </validation-provider>
            </div>
            <div class="d-flex justify-center mt-2">
              <v-btn class="boton-actualizar" depressed :disabled="invalid" @click="guardarContraseña()">
                Cambiar contraseña 
              </v-btn>
            </div>
          </v-form>
        </validation-observer>
      </v-card-text>
    </v-card>
    
    <!-- Componente para mostrar notificación de carga en proceso -->
    <Loading :loading="loading" :text="loadingText"></Loading>

    <!-- notificaciones (alertas) para el usuario de las acciones realizadas -->
    <v-snackbar top v-model="snackbar" :timeout="timeout" :color="color">
      {{ text }}
      <template v-slot:action="{ attrs }">
        <v-btn icon v-bind="attrs" @click="snackbar = false">
          <v-icon>close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>

  </v-main>
</template>

<!-- #################################################################################### -->
<!-- ###### Sección de Scripts                                                     ###### -->
<!-- #################################################################################### -->
<script>
import { Role } from '@/router/role.js';
import { mapMutations, mapState } from 'vuex';
import { required, min } from 'vee-validate/dist/rules';
import Loading from '../../../../components/Loading.vue';
import { extend, ValidationObserver, ValidationProvider } from 'vee-validate';

extend('required', {
  ...required,
  message: 'Este campo es requerido'
});
extend('min', {
  ...min,
  message: 'Requiere 6 dígitos.'
});


export default {
  name: 'Contrasena',
  components: {
    ValidationObserver,
    ValidationProvider,
    Loading,
  },

  data() {
    return {
      roles: Role,
      userRoles: {},
      contrasena: '',
      confirmarContrasena: '',
      show2: false,
      show3: false,
      usuario: '',
      idKeycloack: '',
      tI: null,
      nI: null,
      loadingText: 'Cargando...',
      loading: false,
      snackbar: false,
      text: '',
      timeout: 5000,
      color: 'blue',
    }
  },

  // Se inicializa la variable userRoles con los roles del usuario autenticado
  created() {
    this.usuario = this.auth.username.toUpperCase();
    this.userRoles = this.auth.roles;
    this.tI = this.$store.state.tipoIdentificacion;
    this.nI = this.$store.state.numeroIdentificacion;
    this.listarDatosAfiliado();
  },

  computed: {
    ...mapState(["auth", "tipoIdentificacion", "numeroIdentificacion"]),
  },

  watch: {
    // Observador para la contraseña del usuario
    'contrasena': function (contrasena) {
      this.contrasenaActual = contrasena;
      // Validación personalizada de la contraseña
      extend('contrasenaValida', {
        validate: (value) => value === this.contrasenaActual,
        message: 'Las contraseñas no coinciden'
      });
      // Reinicio del campo de confirmación de contraseña si no coincide con la contraseña actual
      if (contrasena !== this.confirmarContrasena ) {
        this.confirmarContrasena = '';
        const confirmarContrasena = this.$refs.confirmarContrasena;
        if (confirmarContrasena) {
          confirmarContrasena.reset();
        }
      }
    },
  },

  methods: {
    ...mapMutations([]),
    /**
     * Realiza una solicitud al servidor para obtener y listar los datos del afiliado asociados a un tipo y número de identificación.
     * 
     * Recupera los datos del afiliado del servidor y los asigna a las variables correspondientes en la instancia del componente.
     * Si no se encuentran datos del afiliado, no se realiza ninguna asignación y se continúa con la ejecución del código.
     * 
     * En caso de error durante la solicitud al servidor, se maneja el error utilizando el método 'notificacion'.
     */
    async listarDatosAfiliado() {
      const tipoIdentificacion = this.tI;
      const numeroIdentificacion = this.nI;
      try {
        const response = await this.$http.get(`msa-external/api/registro/${tipoIdentificacion}/${numeroIdentificacion}`);
        if (response.data !== "") {
          this.idKeycloack = response.data.idKeycloak;
        }
      } catch (error) {
         this.notificacion(error, 'Error inesperado. Intente más tarde o contacte al administrador.', 'error');
      }
    },
    /**
     * Valida que la tecla presionada sea un número.
     * Evita la entrada de caracteres no numéricos.
     * @param {Event} event - Evento de tecla presionada.
     */
    validarLetra(event) {
      const teclaPresionada = event.key
      if (teclaPresionada.match(/[^0-9]/)) {
        event.preventDefault();
      }
    },
    /**
     * Realiza una solicitud al servidor para cambiar la contraseña del afiliado.
     * Después de la solicitud al servidor, si la respuesta es exitosa, se muestra un mensaje de confirmación.
     * 
     * En caso de error durante la solicitud al servidor, se maneja el error utilizando el método 'notificacion'.
     */
    guardarContraseña() {
      this.loading = true;
      this.$http.get(`msa-external/api/cambiar?idkeycloak=${this.idKeycloack}&newPassword=${this.confirmarContrasena}`)
      .then((response) => {
        this.resetContrasena();
        if (response.data.status === 1) {
          this.notificacion(null, `${response.data.message}.`, 'success');
        }
      })
      .catch((error) => {
        this.notificacion(error, 'Error inesperado. Intente más tarde o contacte al administrador', 'error');
      })
    },
    /**
     * Reinicia los campos relacionados con el cambio de contraseña.
     */
    resetContrasena() {
      this.contrasena = '';
      this.confirmarContrasena = '';
      this.$refs.observerContrasena?.reset();
    },
    /**
     * Muestra la notificación correspondiente.
     * 
     * @param error - Objeto de error.
     * @param mensaje - Mensaje personalizado.
     * @param color - Color de la notificación.
     */
    notificacion(error, mensaje, color) {
      let mensajeError = "";
      if (error && error.response && error.response.status) {
        const status = error.response.status;
        switch (status) {
          case 500:
          case 503:
            mensajeError = "Error interno del servidor (código " + status + "). Intente más tarde o contacte al administrador.";
            break;
          case 404:
            mensajeError = "El recurso solicitado no fue encontrado (código 404).";
            break;
          case 403:
            mensajeError = "Acceso denegado. No tiene permisos suficientes.";
            break;
          case 401:
            mensajeError = "Sesión no autorizada o expirada. Por favor, inicie sesión nuevamente.";
            break;
          case 400:
            if (error.response.data && error.response.data.mensaje) {
              mensajeError = error.response.data.mensaje[0];
            } else if (error.response.data) {
              mensajeError = "Solicitud incorrecta. Verifique los datos ingresados.";
            } else {
              mensajeError = "Error en la solicitud. Verifique los datos ingresados.";
            }
            break;
          default:
            mensajeError = "Ha ocurrido un error inesperado (código " + status + ").";
        } 
      } else {
        mensajeError = mensaje;
      }

      const maxLength = 200;
      if (mensajeError.length > maxLength) {
        mensajeError = mensajeError.substring(0, maxLength) + "...";
      }

      this.text = mensajeError;
      this.color = color;
      this.loading = false;
      this.snackbar = true;
      setTimeout(() => {
        this.observarError(error);
      }, 5500);
    },
    /**
     * Si el error es una 'Network Error' o es una respuesta con estado 401, 
     * establece el estado de error en el almacenamiento para el cierre de sección.
     */
    observarError(error) {
      if (error) {
        if (error.message === 'Network Error') {
          this.$store.commit('setError', true);
        } else if (error.response && error.response.status === 401) {
          this.$store.commit('setError', true);
        } 
      }
    },
  },
}
</script>

<!-- #################################################################################### -->
<!-- ###### Sección de Estilos                                                     ###### -->
<!-- #################################################################################### -->
<style scoped>
.contenido {
  padding: 0 1rem !important; 
  padding-bottom: 1rem; 
  overflow-y: auto; 
  height: 84vh;
}

.descripcion {
  color: rgb(0, 0, 0);
}

.boton-actualizar {
  background-color: #0d47a1 !important;
  color: #fff;
  border-radius: 18px;
}

.fondoDialog {
  background-color: #1867c0;
  color: white;
}
</style>